/**
 * The chips a dashboard shows near its composer. Both views drop the backend's
 * generated chips (they are written from case activity and drift into case
 * prep) and show a curated, context-adapted list instead: client-scoped ones
 * on a client (`clientChips`), practice-level ones on Home (`practiceChips`).
 * "New Suggestions" cycles the curated list locally instead of asking the
 * model for more.
 */
import { useCallback, useMemo, useState } from "react";

import { clientChips } from "./clientSuggestions";
import { practiceChips } from "./practiceSuggestions";
import type {
  ClientDashboard,
  DashboardMode,
  PracticeDashboard,
  Recommendation
} from "./types";
import { useDismissedAlerts } from "./useDismissedAlerts";

type Input = {
  mode: DashboardMode;
  /** The scope's recommendations (rules and generated chips). */
  items: Recommendation[];
  /** True while they load, or while a practice regeneration runs. */
  isLoading: boolean;
  /** The backend refresh; both views rotate their curated list locally instead. */
  regenerate: () => void;
  summary: PracticeDashboard | ClientDashboard | null | undefined;
  summaryLoading?: boolean;
  /**
   * Put the backend's rule chips (real alerts) first. Home turns this off
   * because its "Needs You Today" list already shows them.
   */
  includeAlerts?: boolean;
  /**
   * Let the user dismiss alert chips, remembered under this id (the client's).
   * Omitted, alerts can't be dismissed.
   */
  dismissScope?: string | null;
};

export type DashboardSuggestions = {
  items: Recommendation[];
  isLoading: boolean;
  regenerate: () => void;
  /** Hide an alert chip for good; only set when `dismissScope` was given. */
  dismiss?: (rec: Recommendation) => void;
};

export const useDashboardSuggestions = ({
  mode,
  items,
  isLoading,
  summary,
  summaryLoading = false,
  includeAlerts = true,
  dismissScope = null
}: Input): DashboardSuggestions => {
  const [round, setRound] = useState(0);
  const next = useCallback(() => setRound((r) => r + 1), []);
  const alerts = useDismissedAlerts(dismissScope);
  const waiting = (includeAlerts && isLoading) || (summaryLoading && !summary);
  // Dismissed alerts go before the curated chips are chosen, so the row
  // still fills to its usual count.
  const { visible } = alerts;
  const shown = useMemo(() => visible(items), [visible, items]);
  const chips = useMemo(
    () =>
      mode === "client"
        ? clientChips(
            shown,
            summary as ClientDashboard | null | undefined,
            round
          )
        : practiceChips(
            shown,
            summary as PracticeDashboard | null | undefined,
            round,
            includeAlerts
          ),
    [mode, shown, summary, round, includeAlerts]
  );

  return {
    items: waiting ? [] : chips,
    isLoading: waiting,
    regenerate: next,
    dismiss: dismissScope ? alerts.dismiss : undefined
  };
};
